import { type FC, useEffect } from 'react';
import { Navigate } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';
import { isCoach, type User } from '@/types';
import { track } from '@/utils';

import Faqs from '@/components/GetStarted/MembershipSelect/Faqs';
import HowItWorks from '@/components/GetStarted/MembershipSelect/HowItWorks';
import Logos from '@/components/GetStarted/MembershipSelect/Logos';
import Prices from '@/components/GetStarted/MembershipSelect/Prices';
import Quotes from '@/components/GetStarted/MembershipSelect/Quotes';
import SpecialSauce from '@/components/GetStarted/MembershipSelect/SpecialSauce';

// A coach or paid member can reach the paywall through a remembered post-sign-in
// page, a bookmark, or the back button (MEN-5507). Send a coach to the coach app
// and a paid member where a just-paid member goes: home when already matched,
// otherwise the next onboarding step.
// /users/me returns `coaches`, not `hasCoaches`.
const destinationOffPaywall = (user: User) => {
  if (isCoach(user)) return '/coach';
  if (!user.isPaid && !user.isBusinessPaid) return null;
  return user.userAssesment?.complete && user.coaches.length
    ? '/m'
    : '/get-started/onboarding-video';
};

const MembershipSelectPage: FC<{ user: User }> = ({ user }) => {
  useEffect(() => {
    track('ob_selectplan');
  }, []);

  return (
    <div className="flex w-full flex-1 flex-col overflow-x-hidden bg-sunny-200 pt-20 text-white">
      <Prices user={user} />
      <div className="bg-gradient-to-b from-black to-sage-500 pt-80">
        <HowItWorks />
        <SpecialSauce />
        <Quotes />
      </div>

      <div className="flex flex-col items-center bg-white pt-32 text-black">
        <Faqs />
        <Logos />
      </div>
    </div>
  );
};

const MembershipSelect = () => {
  const { user } = useCurrentUser();

  const destination = user.id ? destinationOffPaywall(user) : null;
  if (destination) {
    return <Navigate to={destination} replace />;
  }

  return <MembershipSelectPage user={user} />;
};

export default MembershipSelect;
